/* eslint-disable react/no-array-index-key */
import React, { useReducer } from 'react';
import PropTypes from 'prop-types';

import { sortArrayOfObjects } from './sort';

import icons from '../../utils/assets/icons';

import './index.scss';

function TableColumn({ head, cells, className, isActive, sortDirection, sortKey, setSort }) {
  const isSortable = Boolean(sortKey);

  return (
    <div className={`Table__column ${className}`}>
      <div
        className={isSortable ? 'Table__head--sort' : 'Table__head'}
        onClick={() => {
          if (isSortable) {
            setSort(sortKey);
          }
        }}
      >
        <div className="caption">{head}</div>

        {isSortable && (
          <div className="Table__sort">
            {isActive && (
              <img
                className={sortDirection ? `Table__chevron--${sortDirection}` : 'Table__chevron'}
                src={icons.chevronDown}
                alt="sort"
              />
            )}
          </div>
        )}
      </div>
      {cells.map((cell, index) => (
        <div key={index} className="Table__cell">
          {cell}
        </div>
      ))}
    </div>
  );
}

TableColumn.propTypes = {
  className: PropTypes.string,
  isActive: PropTypes.bool,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  sortKey: PropTypes.string,
  head: PropTypes.node.isRequired,
  cells: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
  ).isRequired,
  setSort: PropTypes.func.isRequired,
};

TableColumn.defaultProps = {
  className: '',
  isActive: false,
  sortDirection: null,
  sortKey: null,
};

export function reducer(state = {}, action) {
  switch (action.type) {
    case 'direction':
      return { ...state, direction: action.direction };
    case 'key':
      return {
        key: action.key,
        column: action.column,
        direction: 'asc',
      };
    default:
      throw new Error(`action type ${action.type} is not handled by the Table reducer`);
  }
}

export default function Table({ data, columns, withSort, initialSort }) {
  const [sort, dispatch] = useReducer(reducer, initialSort);

  function setSort(colIdx) {
    return key => {
      if (key === sort.key) {
        const direction = sort.direction === 'asc' ? 'desc' : 'asc';
        dispatch({ type: 'direction', direction });
        return;
      }
      dispatch({ type: 'key', key, column: colIdx });
    };
  }

  const shouldBeSorted = Boolean(withSort && sort.direction && sort.key);
  const sortedData = shouldBeSorted ? sortArrayOfObjects(data, sort.direction, sort.key) : data;

  return (
    <div className="Table">
      {columns.map(({ head, component: Component, dataKey, sortKey, className }, columnIndex) => {
        const cells = sortedData.map(el => (Component ? <Component {...el} /> : el[dataKey]));
        return (
          <TableColumn
            key={columnIndex}
            head={head}
            className={className}
            cells={cells}
            isActive={sort.column === columnIndex}
            sortDirection={sort.direction}
            sortKey={sortKey}
            setSort={setSort(columnIndex)}
          />
        );
      })}
    </div>
  );
}

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      head: PropTypes.node,
      dataKey: PropTypes.string,
      sortKey: PropTypes.string,
      component: PropTypes.elementType,
    }),
  ).isRequired,

  initialSort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
    column: PropTypes.number,
  }),
  withSort: PropTypes.bool,
};

Table.defaultProps = {
  initialSort: {},
  withSort: false,
};
